import * as React from 'react';

import { TIME_CONSTANTS, SPEECH_CONSTANTS } from './constants';
import {
  SpeechRecognition,
  SpeechRecognitionEvent,
  SpeechRecognitionErrorEvent,
  UseSpeechRecognitionProps,
  UseSpeechRecognitionReturn,
} from './types/speech-recognition';
import {
  isBraveBrowser,
  isSpeechRecognitionSupported,
  getSpeechRecognitionConstructor,
  checkNetworkConnectivity,
  requestMicrophonePermission,
  configureSpeechRecognition,
  processSpeechResults,
} from './utils/speech-recognition';
import {
  handleSpeechRecognitionError,
  handleSpeechRecognitionStartupError,
  getErrorMessage,
} from './utils/speech-recognition-errors';

export const useSpeechRecognition = ({
  onResult,
  onError,
}: UseSpeechRecognitionProps = {}): UseSpeechRecognitionReturn => {
  const [isListening, setIsListening] = React.useState(false);
  const [speechError, setSpeechError] = React.useState<string | null>(null);
  const recognitionRef = React.useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = React.useRef<string>('');
  const retryCountRef = React.useRef<number>(0);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isStartingRef = React.useRef<boolean>(false);

  // Memoize constants to prevent recalculation
  const maxRetries = React.useMemo(() => SPEECH_CONSTANTS.MAX_RETRIES, []);
  const recognitionTimeout = React.useMemo(
    () => TIME_CONSTANTS.SPEECH_RECOGNITION_TIMEOUT,
    []
  );

  const isSupported = React.useMemo(() => isSpeechRecognitionSupported(), []);

  // Initialize speech recognition
  React.useEffect(() => {
    if (!isSupported) return () => {};

    const SpeechRecognitionConstructor = getSpeechRecognitionConstructor();
    const recognition = new SpeechRecognitionConstructor();
    const isBrave = isBraveBrowser();

    configureSpeechRecognition(recognition, isBrave);

    // Ensure addEventListener exists; if not, provide a minimal shim that maps
    // to the corresponding event handler properties for test environments.
    const target = recognition as unknown as {
      addEventListener?: (
        type: string,
        listener: (event: Event) => void
      ) => void;
      onstart?: (e: Event) => void;
      onresult?: (e: Event) => void;
      onend?: (e: Event) => void;
      onerror?: (e: Event) => void;
    };

    if (typeof target.addEventListener !== 'function') {
      target.addEventListener = (
        type: string,
        listener: (event: Event) => void
      ) => {
        switch (type) {
          case 'start': {
            target.onstart = listener;
            break;
          }
          case 'result': {
            target.onresult = listener as unknown as (e: Event) => void;
            break;
          }
          case 'end': {
            target.onend = listener;
            break;
          }
          case 'error': {
            // eslint-disable-next-line unicorn/prefer-add-event-listener
            target.onerror = listener;
            break;
          }
          default: {
            break;
          }
        }
      };
    }

    target.addEventListener('start', () => {
      setIsListening(true);
      setSpeechError(null);
      finalTranscriptRef.current = '';
      retryCountRef.current = 0;
      isStartingRef.current = false;
      const timeout = isBrave
        ? SPEECH_CONSTANTS.BRAVE_TIMEOUT
        : recognitionTimeout;
      timeoutRef.current = setTimeout(() => {
        setIsListening(false);
        setSpeechError(
          isBrave
            ? 'Speech recognition timed out. Brave browser may block speech recognition due to privacy settings.'
            : 'Speech recognition timed out. Please try again.'
        );
        if (recognitionRef.current)
          try {
            recognitionRef.current.stop();
          } catch {
            /* ignore */
          }
      }, timeout);
    });

    target.addEventListener('result', (event: Event) => {
      const speechEvent = event as SpeechRecognitionEvent;
      processSpeechResults(speechEvent, finalTranscriptRef, onResult);
    });

    target.addEventListener('error', (event: Event) => {
      const errorEvent = event as SpeechRecognitionErrorEvent;
      handleSpeechRecognitionError(errorEvent, {
        setSpeechError,
        setIsListening,
        onError,
        retryCountRef,
        maxRetries,
        recognitionRef,
      });
    });

    target.addEventListener('end', () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      isStartingRef.current = false;
      setIsListening(false);
    });

    recognitionRef.current = recognition;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
          recognitionRef.current.abort();
        } catch {
          /* ignore */
        }
      }
    };
  }, [isSupported, onResult, onError, maxRetries, recognitionTimeout]);

  const checkNetworkAndSetError = (): boolean => {
    if (!checkNetworkConnectivity()) {
      setSpeechError(getErrorMessage('no-connection'));
      return false;
    }
    return true;
  };

  const requestMicrophonePermissionAndSetError = async (): Promise<boolean> => {
    const hasPermission = await requestMicrophonePermission();
    if (!hasPermission) {
      setSpeechError(getErrorMessage('permission-denied'));
      onError?.(getErrorMessage('permission-denied'));
    }
    return hasPermission;
  };

  const startRecognition = async () => {
    if (!recognitionRef.current) {
      setSpeechError(getErrorMessage('not-supported'));
      onError?.(getErrorMessage('not-supported'));
      return;
    }

    if (
      isListening ||
      isStartingRef.current ||
      !checkNetworkAndSetError() ||
      !(await requestMicrophonePermissionAndSetError())
    ) {
      return;
    }

    try {
      setSpeechError(null);
      isStartingRef.current = true;

      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          /* ignore */
        }
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      if (recognitionRef.current) {
        recognitionRef.current.start();
      }
    } catch (error: unknown) {
      isStartingRef.current = false;
      handleSpeechRecognitionStartupError(error, {
        setSpeechError,
        onError,
        recognitionRef,
      });
    }
  };

  const stopRecognition = () => {
    if (!recognitionRef.current) return;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isStartingRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        if (finalTranscriptRef.current.trim() && onResult) {
          onResult(finalTranscriptRef.current.trim());
        }
      } catch {
        setIsListening(false);
      }
    }
  };

  return {
    isListening,
    speechError,
    startRecognition,
    stopRecognition,
    isSupported,
  };
};
